import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { getOffer, pick, type Lang } from '../offers/config';
import { DOCS } from '../legal/docs';
const SITE = 'https://blinkblis.com';
const OG_IMAGE = 'https://cdn.magicpatterns.com/uploads/8L4B6PS8W7uWmJpvwft9Jj/blink-blis-high-resolution-logo-transparent.png';
const HOME: Record<Lang, {
  title: string;
  description: string;
}> = {
  es: {
    title: 'Blinkblis — Decisiones mas inteligentes sobre dinero, credito e IA',
    description: 'Blinkblis ayuda a tomar mejores decisiones sobre dinero, credito, IA y herramientas digitales mediante investigacion transparente, comparaciones y guias practicas.'
  },
  en: {
    title: 'Blinkblis — Smarter Decisions for Money, Credit & AI',
    description: 'Blinkblis helps people make smarter decisions about money, credit, AI and digital tools through transparent research, comparisons and practical guidance.'
  }
};
const LEGAL_DESC: Record<string, Record<Lang, string>> = {
  privacidad: {
    es: 'Como Blinkblis recopila, utiliza y protege la informacion de los usuarios, y que derechos tienes sobre tus datos.',
    en: 'How Blinkblis collects, uses and protects user information, and what rights you have over your data.'
  },
  terminos: {
    es: 'Terminos y condiciones de uso de Blinkblis, incluyendo los limites de nuestro contenido informativo.',
    en: 'Terms and conditions for using Blinkblis, including the limits of our informational content.'
  },
  metodologia: {
    es: 'Como evaluamos productos, que significa nuestro Nivel de Confianza y con que criterios construimos cada recomendacion.',
    en: 'How we evaluate products, what our Confidence Level means and the criteria behind every recommendation.'
  },
  'estandares-editoriales': {
    es: 'Las reglas con las que producimos contenido en Blinkblis: independencia, divulgacion y correccion de errores.',
    en: 'The rules we produce content by at Blinkblis: independence, disclosure and error correction.'
  },
  'divulgacion-de-afiliados': {
    es: 'Como gana dinero Blinkblis, que significa un enlace de afiliado y por que eso no cambia lo que recomendamos.',
    en: 'How Blinkblis makes money, what an affiliate link means and why it does not change what we recommend.'
  }
};
function upsert(selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
}
function setMeta(attr: 'name' | 'property', key: string, value: string) {
  upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  }, el => el.setAttribute('content', value));
}
function setLink(rel: string, hreflang: string | null, href: string) {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  upsert(selector, () => {
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    if (hreflang) l.setAttribute('hreflang', hreflang);
    return l;
  }, el => el.setAttribute('href', href));
}
function clearAlternates() {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
}
export function SeoManager() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useLanguage();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;
  useEffect(() => {
    const clean = pathname.replace(/\/+$/, '') || '/';
    let title = HOME[lang].title;
    let description = HOME[lang].description;
    let canonical = `${SITE}/`;
    let type = 'website';
    let robots = 'index, follow';
    clearAlternates();

    // La ruta de redireccion no debe indexarse nunca.
    if (clean.startsWith('/go/')) {
      document.title = 'Redirecting… | Blinkblis';
      setMeta('name', 'robots', 'noindex, nofollow');
      return;
    }
    const offerMatch = clean.match(/^\/(?:oferta|offer)\/([^/]+)$/);
    const offer = offerMatch ? getOffer(offerMatch[1]) : undefined;
    if (offer) {
      title = `${pick(offer.title, lang)} | Blinkblis`;
      description = pick(offer.subtitle, lang);
      // Cada idioma es canonico de si mismo. No colapsamos uno sobre el
      // otro: son dos paginas con contenido distinto para audiencias
      // distintas, enlazadas entre si con hreflang.
      canonical = lang === 'en' ? `${SITE}/offer/${offer.enSlug}` : `${SITE}/oferta/${offer.slug}`;
      type = 'article';
      setLink('alternate', 'es-US', `${SITE}/oferta/${offer.slug}`);
      setLink('alternate', 'en-US', `${SITE}/offer/${offer.enSlug}`);
      setLink('alternate', 'x-default', `${SITE}/offer/${offer.enSlug}`);
    } else {
      const legalSlug = clean.slice(1);
      const doc = DOCS[legalSlug];
      if (doc) {
        title = `${pick(doc.title, lang)} | Blinkblis`;
        description = LEGAL_DESC[legalSlug]?.[lang] || `${pick(doc.title, lang)} — Blinkblis.`;
        canonical = `${SITE}/${legalSlug}`;
        type = 'article';
      }
    }
    document.title = title;
    document.documentElement.lang = lang;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', robots);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:site_name', 'Blinkblis');
    setMeta('property', 'og:locale', lang === 'en' ? 'en_US' : 'es_US');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', OG_IMAGE);
    setLink('canonical', null, canonical);
  }, [pathname, lang]);
  return null;
}